import React, { useState, useRef, useCallback } from 'react';
import { StyleSheet, TextInput, Animated, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';

export default function FloatingLabelTextarea({ 
  label, 
  value, 
  onChangeText,
  style,
  placeholder,
  height = 150,
  disabled = false,
  ...props 
}) {
  const { colors } = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const labelPosition = useState(new Animated.Value(value ? 1 : 0))[0];
  const labelSize = useState(new Animated.Value(value ? 1 : 0))[0];
  const inputRef = useRef(null);
  const { isXS } = useResponsiveness();

  const inputStyles = StyleSheet.create({
    container: {
      position: 'relative',
      height: height,
    },
    label: {
      position: 'absolute',
      left: 16,
      top: 15,
      fontSize: isXS ? 15 : 14,
      backgroundColor: 'transparent',
      zIndex: 1,
      color: colors.text_color_tertiary,
    },
    input: {
      width: '100%',
      height: '100%',
      paddingHorizontal: 16,
      paddingTop: 16,
      paddingBottom: 12,
      borderWidth: 1,
      borderRadius: 8,
      fontSize: isXS ? 15 : 14,
      backgroundColor: 'transparent',
      textAlignVertical: 'top',
    },
  });

  const handleFocus = () => {
    setIsFocused(true);
    Animated.parallel([
      Animated.timing(labelPosition, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(labelSize, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!value) {
      Animated.parallel([
        Animated.timing(labelPosition, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(labelSize, {
          toValue: 0,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    }
  };

  const handleLabelPress = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  return (
    <View style={[inputStyles.container, style]}>
      <Animated.Text 
        style={[
          inputStyles.label,
          {
            transform: [
              {
                translateY: labelPosition.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, -22],
                }),
              },
              {
                translateX: labelPosition.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, -8],
                }),
              },
              {
                scale: labelSize.interpolate({
                  inputRange: [0, 1],
                  outputRange: [1, 0.75],
                }),
              },
            ],
            color: isFocused ? colors.text_color_primary : colors.text_color_tertiary,
            backgroundColor: colors.solid_color_primary,
            paddingHorizontal: 4,
          },
        ]}
        onPress={handleLabelPress}
      >
        {label}
      </Animated.Text>
      <TextInput
        ref={inputRef}
        style={[
          inputStyles.input,
          {
            borderColor: isFocused ? colors.text_color_primary : colors.border_color_secondary,
            color: colors.text_color_primary,
          },
        ]}
        value={value}
        onChangeText={onChangeText}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={!isFocused && !value ? placeholder : ''}
        placeholderTextColor={colors.color_input_placeholder}
        multiline
        {...props}
      />
    </View>
  );
} 